import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  FormControlLabel,
  Switch,
  Button,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { CMSCustomer } from "../../../../services/cms";
import ImageUpload from "../../../../components/ImageUpload";

interface CustomersDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  editingCustomer: CMSCustomer | null;
  setEditingCustomer: React.Dispatch<React.SetStateAction<CMSCustomer | null>>;
}

const CustomersDialog: React.FC<CustomersDialogProps> = ({
  open,
  onClose,
  onSave,
  editingCustomer,
  setEditingCustomer,
}) => {
  const { t } = useTranslation();

  const handleChange = (field: keyof CMSCustomer, value: any) => {
    if (!editingCustomer) return;
    setEditingCustomer({
      ...editingCustomer,
      [field]: value,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          width: { xs: "95%", sm: "90%", md: "80%" },
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}>
        {editingCustomer?.id
          ? t("admin.contentManagement.editCustomer")
          : t("admin.contentManagement.addCustomer")}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label={t("admin.contentManagement.fields.customerName")}
            fullWidth
            value={editingCustomer?.name || ""}
            onChange={(e) => handleChange("name", e.target.value)}
          />
          <ImageUpload
            currentImage={editingCustomer?.logo_url || ""}
            onImageChange={(url: string) => handleChange("logo_url", url)}
            folder="customers"
            label={t("admin.contentManagement.fields.customerLogo")}
          />
          <TextField
            label={t("admin.contentManagement.fields.websiteUrl")}
            fullWidth
            value={editingCustomer?.website_url || ""}
            onChange={(e) => handleChange("website_url", e.target.value)}
          />
          <TextField
            label={t("admin.contentManagement.fields.description")}
            fullWidth
            multiline
            rows={2}
            value={editingCustomer?.description || ""}
            onChange={(e) => handleChange("description", e.target.value)}
          />
          <TextField
            label={t("admin.contentManagement.fields.displayOrder")}
            fullWidth
            type="number"
            value={editingCustomer?.display_order || 0}
            onChange={(e) => handleChange("display_order", parseInt(e.target.value))}
          />
          <FormControlLabel
            control={
              <Switch
                checked={editingCustomer?.is_active ?? true}
                onChange={(e) => handleChange("is_active", e.target.checked)}
              />
            }
            label={t("admin.contentManagement.active")}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose}>
          {t("admin.contentManagement.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={onSave}
          sx={{
            background: "linear-gradient(135deg, #e91e63 0%, #c2185b 100%)",
          }}
        >
          {t("admin.contentManagement.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomersDialog;